import Grid from '@mui/material/Grid';
import { type InventoryListByIdAPI, ListTypesAPI } from '@openx/types/inventoryList.ts';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { AnyAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { InvalidItems } from '../../shared/BulkAdd/InvalidItems/InvalidItems.tsx';
import type { InvalidItemsError } from '../../shared/BulkAdd/types.ts';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip.tsx';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { setComparisonOpValue } from '../../shared/InventoryAndContent/inventoryHelpers.ts';
import { InventoryListItems } from '../../shared/InventoryListItems/InventoryListItems.tsx';
import type { SetInventoryListItemsDispatch } from '../InventoryAndContentTopBox/constants.ts';
import { DefaultItems } from './DefaultItems.tsx';

const invalidItemsStyles = {
  '.MuiPaper-root.MuiAlert-standard': {
    alignItems: 'center',
    width: '100%',
    marginTop: 1,
  },
};

type AppBundleIdProps = {
  readonly?: boolean;
  targetingParams: InventoryAndContentState;
  dispatchInventory: (action: AnyAction) => void;
  isNotLastItem: boolean;
  switchState: boolean;
  accountUid: string;
  appBundleIdDuplicateErrors: InvalidItemsError | null;
  setInventoryListItems: SetInventoryListItemsDispatch;
  inventoryListItems: InventoryListByIdAPI['values'] | undefined;
  showDimensionChipLine?: boolean;
  disabledReasonMessage?: string;
};

export const AppBundleId = ({
  readonly = true,
  targetingParams,
  dispatchInventory,
  isNotLastItem,
  switchState,
  accountUid,
  appBundleIdDuplicateErrors,
  setInventoryListItems,
  inventoryListItems,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: AppBundleIdProps) => {
  const { isOa } = useTargetingContext();

  const inventoryListId = targetingParams?.[InventoryContentOption.APP_BUNDLE_ID]?.inventorylist_id;
  const appBundleIds =
    !!targetingParams?.[InventoryContentOption.APP_BUNDLE_ID]?.val?.[ComparisonType.EQUALS]?.length ||
    !!targetingParams?.[InventoryContentOption.APP_BUNDLE_ID]?.val?.[ComparisonType.BEGINS_WITH]?.length;

  const showDimensionChip = isOa && inventoryListId && appBundleIds;
  const showDuplicateError = isOa && !!appBundleIdDuplicateErrors?.invalidItems?.length;
  const onChangeInventoryList = useCallback(() => {
    dispatchInventory({
      type: setComparisonOpValue.app_bundle_id,
      payload: {
        comparisonType: targetingParams[InventoryContentOption.APP_BUNDLE_ID].op,
        inventorylist_id: null,
      },
    } as unknown as AnyAction);
  }, [dispatchInventory, targetingParams]);

  return (
    <>
      {showDuplicateError && (
        <Grid sx={invalidItemsStyles}>
          <InvalidItems errors={[appBundleIdDuplicateErrors]} />
        </Grid>
      )}

      <DefaultItems
        readonly={readonly}
        dispatchInventory={action => {
          dispatchInventory(action);
        }}
        targetingParams={targetingParams}
        option={InventoryContentOption.APP_BUNDLE_ID}
        key={InventoryContentOption.APP_BUNDLE_ID}
        isNotLastItem={isNotLastItem}
        switchState={switchState}
        inventoryListCount={inventoryListItems?.length}
        dataTest={'app-bundle-id'}
        disabledReasonMessage={disabledReasonMessage}
      />

      {showDimensionChip && (
        <DimensionChip isInsideOption targetingDimension={targetingParams[InventoryContentOption.APP_BUNDLE_ID].op} />
      )}

      {isOa && (
        <InventoryListItems
          listType={ListTypesAPI.BUNDLE}
          readonly={readonly}
          accountUid={accountUid}
          inventorylistId={targetingParams[InventoryContentOption.APP_BUNDLE_ID].inventorylist_id}
          setInventoryListItems={setInventoryListItems}
          onChange={onChangeInventoryList}
        />
      )}

      {isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={switchState} />}
    </>
  );
};
